import { NzSelectComponent } from 'ng-zorro-antd/select';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges, OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR} from '@angular/forms';
import { InputBoolean } from 'ng-zorro-antd/core/util';
import { NzMessageService } from 'ng-zorro-antd/message';
import {cloneDeep, isArray, uniq, uniqBy} from "lodash";
import {BehaviorSubject, Observable, of, Subject} from "rxjs";
import {debounceTime, switchMap, takeUntil} from "rxjs/operators";
import { TranslateService } from '@ngx-translate/core';
import { AbstractControlComponent, OptionModel, Ultilities } from 'projects/vss-ui/src/public-api';

@Component({
  selector: 'cs-select-custom',
  templateUrl: './cs-select-custom.component.html',
  styleUrls: ['./cs-select-custom.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CsSelectCustomComponent),
      multi: true
    }
  ],
  encapsulation: ViewEncapsulation.None,
})
export class CsSelectCustomComponent extends AbstractControlComponent<any | any[]> implements OnInit, OnChanges, ControlValueAccessor, OnDestroy, AfterViewInit {
  private _destroy$: Subject<void> = new Subject<void>()
  @Input() isLoading = false;
  pageLoad = { page: 1, size: 20 };
  @Input() csDropdownRenderTpl!: TemplateRef<any>;
  @Input() isErrorSelect: boolean = false;
  @Input() controlValue: any;
  @Input() optionList: any[] = [];
  @Input() csMode: 'default' | 'multiple' | 'tags' = 'default';
  @Input() csAllowClear: boolean = true;
  @Input() csDropdownMatchSelectWidth: boolean = false
  @Input() csShowSearch: boolean = true;
  @Input() csShowId: boolean = false;
  @Input() csIsMultiLevel: boolean = false;
  @Input() typeReturnMultilevel: 'all' | 'child' = 'child';
  @Input() csMaxTagCount!: number;
  @Input() keySearch!: string;
  @Input() params: any;
  @Input() paramsExpand: any;
  @Input() suffixLabelSelected: string = '';
  @Input() fieldId: string = 'id';
  @Input() fieldLabel: string = 'name';
  @Input() @InputBoolean() valueObject!: boolean;
  @Input() getOptionsApi!: ((params: OptionModel) => Promise<any[]>) | undefined;
  @Input() getExpandOptionsApi!: ((paramsExpand: OptionModel) => Promise<any[]>) | undefined;
  @Input() checkDisable = (option: OptionModel) => false;
  @Input() isResetControlOnChangeParams = true;
  @Input() isAllowChangeOnEmpty = false; // allow to emit csChange when value is empty array
  @Input() isNumberOnly = false;
  @Input() getOptionsDefaultApi!: ((valuesSelected: any[]) => Observable<any>) | undefined;
  @Input() selectAllChecked = false;
  @Output() onClose = new EventEmitter<boolean>()
  @Output() onOpen = new EventEmitter<boolean>();
  @Output() csOpenChange = new EventEmitter();
  @Output() onInputChange = new EventEmitter();
  @Output() onInputChangeFullValue = new EventEmitter();
  @Output() isCheckSelectAll = new EventEmitter();
  @Output() dataAfterExpand = new EventEmitter();
  @Input() isShowUserIcon: boolean = false
  @Input() mode = 'default'
  @Input() csPlaceHolder: any = this.translate.instant('BASE.SELECT')
  @Input() isCheckLevel: boolean = false;
  @Input() isLazyLoadTree: boolean = false;
  @Input() disableSelectAll: boolean = false;
  @ViewChild('nzSelectRef') nzSelectRef!: NzSelectComponent
  REGEX_TEXT = /[^a-z0-9 ]/gi
  isClose = false

  options: any[] = []
  selectedValues: any[] = []
  fullValueSelected: any;
  selectedItem: any = null;
  // isNextLevel: boolean = false;
  keywordBind: BehaviorSubject<string> = new BehaviorSubject<string>('')
  pagingForm!: FormGroup
  cancelClose = false
  keywordSearch = ''

  // get selectedValuesAsync() {
  //   let data: any = this.findObjectByValue(this.options, this.selectedValues.map((selectedValues: any) => (selectedValues.value)));
  //   return data?.map((x: any) => ({ value: x?.value, label: x?.label }))
  // }

  get selectedValuesAsync() {
    return this.selectedValues?.map((x: any) => ({
      value: x?.value,
      label: x?.label || x?.labelHtml
    })) as any || []
  }

  constructor(
    private message: NzMessageService,
    private fb: FormBuilder,
    public translate: TranslateService
    ) {
    super();
  }

  ngOnInit() {
    this.options = cloneDeep(this.optionList)
    this.loadMore();
    this.pagingForm = this.fb.group({
      page: this.fb.control(1),
      size: this.fb.control(20),
    })
    this.pagingForm.valueChanges
      .pipe(
        takeUntil(this._destroy$),
        switchMap(paging => {
          return this.apiStreamSearch({
            ...(this.params || {}),
            keyword: this.keywordBind.value,
            ...paging,
          })
        })
      ).subscribe()
    this.keywordBind.pipe(
      takeUntil(this._destroy$),
      debounceTime(300)
    ).subscribe(() => this.pagingForm.patchValue({page: 1}))
    if(this.csIsMultiLevel) {
      this.loadItemActive()
    }
  }

  checkSelected (item: OptionModel) {
    return this.selectedValues.some(s => s.value == item.value)
  }

  loadItemActive() {
    const findInChild = (c: any) => {
      if(c.value == this.controlValue) {
        return c
      } else if (c?.children?.length) {
        c.children.forEach((i: any) => {
          if(findInChild(i)) {
            c.expanded = true
          }
        })
      }
    }
    this.optionList?.forEach(op => {
      findInChild(op)
    })
  }

  ngOnDestroy() {
    this._destroy$.next()
    this._destroy$.complete()
    this._destroy$.unsubscribe()
  }

  ngAfterViewInit() {
    this.getDefaultOptions(this.controlValue)
  }

  getDefaultOptions(values: any) {
    if (!values || (Array.isArray(values) && values.length == 0)) { return; }
    if(this.getOptionsDefaultApi){
      this.getOptionsDefaultApi(Array.isArray(values) ? values : `${typeof values == 'string' || values ? values : ''}`?.split(',')).subscribe(x=>{
        if(x?.data){
          this.options = uniqBy(this.options.concat([x?.data]), 'value')
        }
      })
    }
  }

  apiStreamSearch = (params: any) : Observable<any[]> => {
    return of([])
  }

  async writeValue(obj: any) {
    super.writeValue(obj);

    if (!obj || (Array.isArray(obj) && obj.length == 0 && !this.isAllowChangeOnEmpty)) { return; }
    this.getDefaultOptions(obj)
    this.onModelChange(obj);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.params && !changes?.params.firstChange) {
      this.pageLoad.page = 1;
      if (this.isResetControlOnChangeParams) {
        this.writeValue(null)
        this.onModelChange(null);
        this.optionList = [];
        this.onSearch('')
      }
      this.loadMore();
    }
    if (changes?.optionList && !changes?.optionList?.firstChange) {
      this.onSearch('')
      if(this.csIsMultiLevel) {
        this.loadItemActive()
      }
    }
  }

  loadMorePage() {
    if (this.isLoading) { return; }
    this.loadMore()
    this.pagingForm?.patchValue({page: this.pagingForm.value.page + 1})
  }

  async loadMore(): Promise<void> {
    this.isLoading = true;
    this.params = {
      ...this.params,
      ...this.pageLoad
    }
    try {
      if(this.getOptionsApi != undefined) {

        const options = await this.getOptionsApi(this.params);
        if(this.pageLoad.page == 1) {
          this.options = Ultilities.distinctData(Ultilities.convertNzOption(options, this.fieldId, this.fieldLabel, this.csShowId), 'value');
          if (Array.isArray(this.selectedValues) && this.selectedValues?.length) {
            let data: any = this.selectedValues.map((x: any) => x?.value)
            this.getCheckedItem(this.options, data);
            if (this.selectAllChecked && this.csMode === 'multiple') {
              this.selectedValues = this.options;
              let data: any = this.selectedValues.map((x: any) => x?.value)
              this.getCheckedItem(this.options, data);
            }
          }
          this.pageLoad.page++;
        } else if (options.length > 0) {
          this.options = Ultilities.distinctData(this.options.concat(Ultilities.convertNzOption(options, this.fieldId, this.fieldLabel, this.csShowId)), 'value');
          if (Array.isArray(this.selectedValues) && this.selectedValues?.length) {
            let data: any = this.selectedValues.map((x: any) => x?.value)
            this.getCheckedItem(this.options, data);
            if (this.selectAllChecked && this.csMode === 'multiple') {
              this.selectedValues = this.options;
              let data: any = this.getAllValueEnable(this.options);
              this.writeValue(data)
              this.onModelChange(data)
              this.inputChange(data)
            }
          }
          this.pageLoad.page++;
        }
      }
    } catch (error: any) {
      this.message.error(error?.error?.error);
    } finally {
      this.isLoading = false;
    }
    // if(!this.keywordSearch) {
    //   if(Array.isArray(this.controlValue)) {
    //     let value = null
    //     value = this.controlValue.filter(v => this.options.find(o => o.value == v))
    //     if(value.length != this.controlValue.length) {
    //       this.writeValue(value)
    //       this.onModelChange(value);
    //     }
    //   } else if(this.options.find(o => o.value != this.controlValue)) {
    //     this.writeValue(null)
    //     this.onModelChange(null);
    //   }
    // }
  }

  searchText(text: string, valueCompare: string): boolean {
    return Ultilities.removeVietnameseAccents(`${text ? this.translate.instant(text) : text}`.trim()).includes(Ultilities.removeVietnameseAccents(`${valueCompare ? this.translate.instant(valueCompare) : valueCompare}`.trim()))
  }

  onSearch(value: any) {
    this.keywordSearch = value
    if (this.isNumberOnly && !Ultilities.validateNunber(value)) {
      this.options = [];
      return;
    }
    const newValue = this.getOptionsApi ? Ultilities.replaceSpecial(value) : value
    if(!this.getOptionsApi) {
      const searchDeep = (op: any) => {
        if(this.searchText(op.label, newValue)) {
          return op
        }
        if(op.children?.length && op.children.find((c: any) => searchDeep(c))) {
          return {
            ...op,
            expanded: true,
            children: op.children?.filter((c: any) => searchDeep(c)).map((c: any) => searchDeep(c))
          }
        }
        return null
      }
      this.options = this.optionList?.filter(x => !!searchDeep(x)).map(x => searchDeep(x))
    } else {
      this.pageLoad.page = 1;
      if (this.keySearch) {
        this.params[this.keySearch] = newValue;
      }
      this.loadMore();
    }
    this.keywordBind.next(newValue)
  }

  onModelChange(value: any) {
    if (Array.isArray(value)) {
      if (value.length) {
        this.getCheckedItem(this.options, value)
        this.selectAllChecked = this.getCheckedAllItem(this.options, value);
      } else {
        this.selectAllChecked = false;
      }
    } else {
      this.options = this.options.map((options: any) => ({...options, itemChecked: options.value === value}))
    }
    if (this.disableSelectAll && this.csMode === 'multiple') {
      this.selectAllChecked = true
    }
    const listOptions: any[] = []
    const getChild = (o: any) => {
      if((Array.isArray(value) && value.length > 0 && value.includes(o.value)) || value == o.value) {
        if (!o?.isDisable) {
          o.itemChecked = true
        }
      } else {
        o.itemChecked = false
      }
      listOptions.push(o)
      o?.children?.forEach((c: any) => getChild(c))
    }
    this.options?.forEach(o => getChild(o))

    if(this.csMode == 'multiple') {
      const arr = Array.isArray(value) ? value : [];
      this.selectedValues = uniqBy(arr?.map((x: any) => [...listOptions, ...this.selectedValues]?.find((o: any) => o.value == x) || {value: x, label: x}), 'value')
    } else if(this.fullValueSelected) {
      this.fullValueSelected = uniqBy([...listOptions, this.fullValueSelected]?.find((o: any) => o.value == value) || {value: value, label: value}, 'value')
    } else {
      this.selectedValues = uniqBy([...listOptions, ...this.selectedValues]?.filter((o: any) => o.value == value) || {value: value, label: value}, 'value')
    }
    this.onChangeFn(value);
    let data: any = listOptions?.find(c => c.value == value) ? {
      ...listOptions?.find(c => c.value == value)
    } : null
    if (data) {
      data.options = data?.init?.find((x: any) => x[this.fieldId] === data.value)?.options;
      data.fullValue = data?.init?.find((x: any) => x[this.fieldId] === data.value);
      // delete data.init;
    }
    this.csChange.emit(data || value);

    this.updateSelectAll();
  }

  openChange(event: boolean) {
    if(this.cancelClose) {
      this.cancelClose = false
      this.nzSelectRef.setOpenState(true)
      return
    }
    if(event) {
      this.loadItemActive()
      this.isClose = false
      this.onOpen.emit(true)
    } else {
      this.isClose = true
      this.onClose.emit(true)
    }
    this.csOpenChange.emit(event);
  }

  inputChange(val: any) {
    if (Array.isArray(val)) {
      if (val.length) {
        this.getCheckedItem(this.options, val)
        this.selectAllChecked = this.getCheckedAllItem(this.options, val);
      } else {
        this.selectAllChecked = false;
      }
    } else {
      this.options = this.options.map((options: any) => ({...options, itemChecked: options.value === val}))
    }
    if (this.disableSelectAll && this.csMode === 'multiple') {
      this.selectAllChecked = true
    }
    const data = this.options.find(x => x.value === val) ? {
      ...this.options.find(x => x.value === val)
    } : null
      if (data) {
        data.options = data?.init?.find((x: any) => x[this.fieldId] === data.value)?.options;
        data.fullValue = data?.init?.find((x: any) => x[this.fieldId] === data.value);
      }
      if(this.csMode == 'multiple') {
        this.onInputChangeFullValue.emit(this.selectedValues);
      } else {
        this.onInputChangeFullValue.emit(data);
      }
    this.onInputChange.emit(val);
  }

  get fullValueSelectedAsync() {
    return this.fullValueSelected?.map((x: any) => this.options.find((o: any) => o.value == x.value) || x)
  }

  objExpand: any = {};
  nextLevel(item: any) {
    this.cancelClose = true
    item.expanded = !item.expanded
    this.onChangeExpand(item, item.value);
    // this.writeValue(item?.value)
    // this.selectedValues = [item]
    // this.onModelChange(item?.value)
  }

  async onChangeExpand(item: any, id: any) {
    let keys = Object.keys(this.objExpand);
    if(!keys.includes(id) && !item?.isDisable) keys.push(id);
    keys.forEach((key: any) => {
      this.objExpand[key] = id === key;
    });
    if (this.isLazyLoadTree && item?.children?.length === 0) {
      try {
        this.isLoading = true;
        if(this.getExpandOptionsApi != undefined) {
          let paramsExpand = {
            ...this.paramsExpand,
            isExpanded: true,
            parentId: id
          }
          const options = await this.getExpandOptionsApi(paramsExpand);
          if (options.length) {

          }
          if (this.selectAllChecked) {
            this.selectedValues = this.selectedValues.concat(options.filter((options) => !options?.isDisable || options?.itemChecked));
          }
          this.getOption(this.options, id, options);
          this.options = cloneDeep(this.options);
          this.dataAfterExpand.emit(this.options);
          if (this.csMode === 'multiple') {
            this.onChangeSelect(this.selectedValues.map((selectedValues: any) => selectedValues?.value));
          }
          // this.getCheckedItem(this.options, this.selectedValues.map((selectedValues: any) => (selectedValues?.value)));
        }
      } catch (error: any) {
        this.message.error(error?.error?.error);
      } finally {
        this.isLoading = false;
      }
    }
  }

  onChangeSelect(value: any) {
    let keys = Object.keys(this.objExpand);
    keys.forEach((key: any) => {
      if(this.objExpand[key]) {
        let k = Number.parseInt(key);
        if(value.includes(k)) value = value.filter((x: any) => x !== k);
        else value.push(k);
        this.objExpand[key] = false;
      }
    });
    this.controlValue = value;
    if (!(this.csMode === 'multiple') || (Array.isArray(value) && value.length === 0) || !value) {
      this.onModelChange(value);
      this.inputChange(value);
    } else {
      if (this.csIsMultiLevel) {
        if (!this.selectedItem) {
          this.selectedItem = this.getSelectedItem(this.selectedValues, value);
        }
        if (this.isCheckLevel && this.selectedItem) {
          value = !this.selectedItem.itemChecked ?
          (value?. filter((s: any) => s != this.selectedItem.value) || []).concat([this.selectedItem.value].concat(this.getAllValueLevel(this.options, this.selectedItem.level))) :
          value?. filter((s: any) => s != this.selectedItem.value && !this.getAllValueLevel(this.options, this.selectedItem.level).includes(s)) || []
          this.selectedItem = null;
        }
        this.controlValue = uniq(value);
        this.onModelChange(uniq(value));
        this.inputChange(uniq(value));
      } else {
        let data: any = this.findObjectByValue(this.options, value);
        if (data && data.length) {
          data.forEach((data: any) => {
            this.changeCheckBox(true, data);
          })
        }
      }
    }
  }

  changeCheckBox (e: boolean, item: any) {
    item.itemChecked = e
    let dataChange: any = null
    if(this.csMode !='multiple') {
      dataChange = e ? item.value : null
    } else {
      if (this.isCheckLevel) {
        dataChange = e ?
       (this.controlValue?. filter((s: any) => s != item.value) || []).concat([item.value].concat(this.getAllValueLevel(this.options, item.level))) :
       this.controlValue?. filter((s: any) => s != item.value && !this.getAllValueLevel(this.options, item.level).includes(s)) || []
      } else {
        dataChange = e ?
        (this.controlValue?. filter((s: any) => s != item.value) || []).concat([item.value].concat(this.getAllValueEnable(item.children)).concat(this.getParentValue(this.options, item.value))) :
         this.controlValue?. filter((s: any) => s != item.value && !this.getAllValueEnable(item.children).includes(s) && !this.findParentValues(this.options, item).includes(s)) || []
      }
    }
    this.writeValue((isArray(dataChange) && dataChange?.length) ? uniq(dataChange) : dataChange)
    this.onModelChange((isArray(dataChange) && dataChange?.length) ? uniq(dataChange) : dataChange)
    this.inputChange((isArray(dataChange) && dataChange?.length) ? uniq(dataChange) : dataChange)
    this.updateSelectAll();
    if (!(this.csMode === 'multiple')) {
      this.nzSelectRef.setOpenState(false)
    }

  }

  selectItem(item: any) {
    this.selectedItem = item;
  }

  changeCheckAll (e: boolean) {
    if (!this.disableSelectAll) {
      this.selectAllChecked = e;
      let dataChange: any = null
      if (e) {
        this.options = this.getOptionCheckedAllItem(this.options);
        dataChange = this.getAllValueEnable(this.options);
      } else {
        dataChange = [];
        this.options = this.getOptionCheckedAllItem(this.options);
      }
      this.writeValue(dataChange)
      this.onModelChange(dataChange)
      this.inputChange(dataChange)
    }
  }

  updateSelectAll() {
    if (!this.disableSelectAll) {
      if (this.selectedValues.length && this.getValueEnable(this.options).length) {
        this.selectAllChecked = this.selectedValues.length === this.getValueEnable(this.options).length;
        this.isCheckSelectAll.emit(this.selectAllChecked)
      } else {
        this.selectAllChecked = false;
        this.isCheckSelectAll.emit(this.selectAllChecked)
      }
    } else {
      this.selectAllChecked = this.csMode === 'multiple'
    }
  }

  getAllValue(options: OptionModel[] | any, result: any = []) {
    if (Array.isArray(options) && options.length) {
      options.forEach((item: any) => {
        result.push(item.value);
        if (item?.children && item?.children.length > 0) {
          this.getAllValue(item?.children, result);
        }
      });
      return result;
    }
    return result;
  }

  findParentValues(arr: any, target: any) {
    let parentValues: any = [];

    const search = (arr: any, parents: any) => {
      for (let item of arr) {
          if (item.label === target.label && item.value === target.value) {
              parentValues = [...parents];
              break;
          }
          if (item.children?.length > 0) {
              search(item.children, [...parents, item.value]);
          }
      }
  }

    search(arr, []);
    return parentValues;
  }

  getParentValue(data: any, targetValue: any) {
    const parentValues = [];
    for (let i = 0; i < data.length; i++) {
        const item = data[i];
        if (item?.children && item?.children.length > 0) {
            const targetIndex = item?.children.findIndex((child: any) => child.value === targetValue);
            if (targetIndex !== -1) {
                const siblings = [...item?.children.slice(0, targetIndex), ...item?.children.slice(targetIndex + 1)];
                const allSiblingsChecked = siblings.every(sibling => sibling.itemChecked);
                if (item?.children[targetIndex].itemChecked && allSiblingsChecked && !item?.isDisable) {
                  parentValues.push(item.value);
                }
            }
            const parentValuesInChildren: any = this.getParentValue(item.children, targetValue);
            parentValues.push(...parentValuesInChildren);
        }
    }
    return parentValues;
  }

  getCheckedItem(listData: any[], selectedItem: any) {
    if (Array.isArray(selectedItem) && selectedItem.length) {
      listData?.forEach((item: any) => {
        item.itemChecked = selectedItem.includes(item.value);
        if (item.children && item.children.length > 0) {
            this.getCheckedItem(item.children, selectedItem);
        }
      });
    } else {
      listData?.forEach((item: any) => {
        item.itemChecked = selectedItem === item.value;
        if (item.children && item.children.length > 0) {
            this.getCheckedItem(item.children, selectedItem);
        }
      });
    }
    return listData;
  }

  getCheckedAllItem(listData: any[], selectedItem: any) {
    let sortedListData = this.getValueEnable(listData)?.slice()?.sort();
    let sortedSelectedItem = selectedItem?.slice()?.sort();

    if (JSON.stringify(sortedListData) === JSON.stringify(sortedSelectedItem)) {
      return true;
    }
    return false;
  }

  findObjectByValue(array: any, value: any) {
    if (Array.isArray(value)) {
      let result = [];
      for (let i = 0; i < array.length; i++) {
          if (value.includes(array[i].value)) {
              result.push(array[i]);
          }
          if (array[i]?.children && array[i]?.children.length > 0) {
              const childResults: any = this.findObjectByValue(array[i]?.children, value);
              if (childResults.length > 0) {
                  result = result.concat(childResults);
              }
          }
      }
      return result;
    } else {
      for (let i = 0; i < array.length; i++) {
          if (array[i].value === value) {
              return array[i];
          }
          if (array[i]?.children && array[i]?.children.length > 0) {
              const result: any = this.findObjectByValue(array[i]?.children, value);
              if (result) {
                  return result;
              }
          }
      }
      return null;
    }
  }

  getOptionCheckedAllItem(listData: any[]) {
    listData.forEach((item: any) => {
      if (!item?.isDisable) {
        item.itemChecked = true;
      } else {
        item.itemChecked = false;
      }
      if (item.children && item.children.length > 0) {
          this.getOptionCheckedAllItem(item.children);
      }
    });
    return listData;
  }

  getValueEnable(options: OptionModel[] | any, result: any = []) {
    if (Array.isArray(options) && options.length) {
      options.forEach((item: any) => {
        if (!item?.isDisable || item?.itemChecked) {
          result.push(item.value);
        }
        if (item?.children && item?.children.length > 0) {
          this.getValueEnable(item?.children, result);
        }
      });
      return result;
    }
    return result;
  }

  getAllValueEnable(options: OptionModel[] | any, result: any = []) {
    if (Array.isArray(options) && options.length) {
      options.forEach((item: any) => {
        if (!item?.isDisable) {
          result.push(item.value);
        }
        if (item?.children && item?.children.length > 0) {
          this.getAllValueEnable(item?.children, result);
        }
      });
      return result;
    }
    return result;
  }

  getAllValueLevel(options: OptionModel[] | any, level: any, result: any = []) {
    if (Array.isArray(options) && options.length) {
      options.forEach((item: any) => {
        if (!item?.isDisable && item?.level === level) {
          result.push(item.value);
        }
        if (item?.children && item?.children.length > 0) {
          this.getAllValueLevel(item?.children, level, result);
        }
      });
      return result;
    }
    return result;
  }

  getSelectedItem(selectedValues: any[], value: any[]) {
    if (Array.isArray(selectedValues) && Array.isArray(value) && selectedValues.length > value.length) {
      for(let i = 0; i < selectedValues.length; i++) {
        if (!value.includes(selectedValues[i].value)) {
          return selectedValues[i];
        }
      }
    } else {
      let data: any[] = selectedValues.map((selectedValues: any) => (selectedValues.value));
      for(let i = 0; i < value.length; i++) {
        if (!data.includes(value[i])) {
          return this.findObjectByValue(this.options, value[i]);
        }
      }
    }
    return null;
  }

  getOption(options: any[] | any, id: any, value: any[]) {
    for (let i = 0; i < options.length ; i++) {
      if (options[i]?.id === id) {
        options[i].children = options[i]?.children?.concat(value);
        break;
      }
      if (options[i]?.children && options[i]?.children.length > 0) {
        this.getOption(options[i]?.children, id, value);
      }
    }
  }

  uniqValue(value: any) {
    return uniq(value);
  }

  getName(event:string){
    const name = event?.split(' - ')[1]?.split(' ')
    return name[0][0] + name[name.length - 1][0]
  }
}
